import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowFatLineUpIcon } from '@phosphor-icons/react/dist/csr/ArrowFatLineUp';
import { ChatCircleTextIcon } from '@phosphor-icons/react/dist/csr/ChatCircleText';
import { CheckCircleIcon } from '@phosphor-icons/react/dist/csr/CheckCircle';
import { LockSimpleIcon } from '@phosphor-icons/react/dist/csr/LockSimple';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { Button } from '../../design-system/primitives/Button/Button';
import { Textarea } from '../../design-system/primitives/Textarea/Textarea';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import { escalationsApi, type EscalationRow } from '../../lib/api/escalations';
import { mapDetails } from '../reference/referenceFormHelpers';
import { ACTION_SUBMIT_KEY, ACTION_TITLE_KEY, type EscalationActionKind } from './escalationLabels';
import styles from './EscalationActionDialog.module.css';

/** The same glyph per action as the detail screen's buttons that open this dialog. */
const ACTION_ICON = {
  advance: <ArrowFatLineUpIcon size={16} />,
  resolve: <CheckCircleIcon size={16} />,
  close: <LockSimpleIcon size={16} />,
  comment: <ChatCircleTextIcon size={16} />,
} satisfies Record<EscalationActionKind, unknown>;

const FORBIDDEN_CODE = 'forbidden';

/**
 * The one dialog behind all four transitions — `advance`/`resolve`/`close`/
 * `comment` (M-10, Task 5, spec E-3, E-4). One optional `notes` field,
 * exactly `EscalationActionRequest`'s own shape, and one call site per
 * `kind` into `escalationsApi`.
 *
 * ## Actions come from the server, never re-derived here (Global Constraint)
 *
 * This dialog does not decide whether a transition is ALLOWED — `advance`
 * at level 3, a terminal escalation refusing every verb, a blank `close` at
 * level 3 — every one of those is a `409`/`422` this component surfaces
 * exactly as the server worded it, through the same `describeFailure`/
 * `mapDetails` split `LogCallDialog.tsx` already uses. `EscalationDetail.tsx`
 * hides the OBVIOUSLY dead buttons (a terminal record offers none of the
 * four; `advance` is not offered at level 3) — this dialog itself predicts
 * nothing further.
 *
 * ## The server's refusal is not always keyed to a field this form renders
 *
 * `notes` is the only field here. A `409` for "already resolved/closed"
 * names `status`; the level-3 `advance` refusal names `level` — neither is
 * `notes`, so both go to the failure banner rather than being silently
 * dropped into `fieldErrors`, the identical split `LogCallDialog.tsx`'s own
 * docblock explains for `call`.
 */

export interface EscalationActionDialogProps {
  open: boolean;
  /** Which of the four transitions this render answers. Only meaningful while `open` — see this file's own docblock. */
  kind: EscalationActionKind;
  /** `null` while nothing is being acted on — this dialog is not mounted with `open` in that state, but the type still allows it so a caller cannot pass a stale uuid by construction. */
  escalationUuid: string | null;
  /** The escalation's own mobile number, so the dialog's title names the record rather than asking a bare "Are you sure?" (`Dialog`'s own development guard). */
  mobile: string;
  onClose: () => void;
  onDone: (updated: EscalationRow) => void;
}

export function EscalationActionDialog({
  open,
  kind,
  escalationUuid,
  mobile,
  onClose,
  onDone,
}: EscalationActionDialogProps) {
  const { t } = useTranslation();

  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const inFlight = useRef(false);

  useEffect(() => {
    if (!open) return;
    setNotes('');
    setFailure(null);
    setFieldErrors({});
  }, [open]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('escalations.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('escalations.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('escalations.actionFailed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current || escalationUuid === null) return;

    inFlight.current = true;
    setSubmitting(true);
    setFailure(null);
    setFieldErrors({});

    const input = { notes: notes.trim() === '' ? null : notes.trim() };

    try {
      const updated = await (kind === 'advance'
        ? escalationsApi.advance(escalationUuid, input)
        : kind === 'resolve'
          ? escalationsApi.resolve(escalationUuid, input)
          : kind === 'close'
            ? escalationsApi.close(escalationUuid, input)
            : escalationsApi.comment(escalationUuid, input));
      onDone(updated);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;

      const mapped = mapDetails(error.details);

      // THE SERVER'S REFUSAL IS NOT ALWAYS KEYED TO `notes` — see this
      // file's own docblock. `status` (already terminal) and `level`
      // (advance exhausted) are neither `notes`, so both surface in the
      // banner instead of being dropped on the floor.
      const banner = Object.entries(mapped)
        .filter(([field]) => field !== 'notes')
        .map(([, message]) => message);

      setFieldErrors(mapped);

      if (banner.length > 0) {
        setFailure(banner.join(' '));
      } else if (Object.keys(mapped).length === 0) {
        setFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} kind="form" onClose={onClose} title={t(ACTION_TITLE_KEY[kind], { mobile })}>
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div className={styles['failure']} role="alert" aria-atomic="true">
          {failure}
        </div>

        <Textarea
          label={t('escalations.notesLabel')}
          dir="auto"
          value={notes}
          error={fieldErrors['notes']}
          onChange={(event) => {
            const { value } = event.target;
            setNotes(value);
          }}
        />

        <div className={styles['actions']}>
          <Button type="submit" loading={submitting} icon={ACTION_ICON[kind]}>
            {t(ACTION_SUBMIT_KEY[kind])}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} icon={<XIcon size={16} />}>
            {t('escalations.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
